import { useState, useEffect, useCallback, useContext } from "react";
import { supabase } from "./supabaseClient.js";
import { ThemeContext } from "./ThemeContext.jsx";
import { isUserOnline } from "./presence.js";

// ─── Friend System ──────────────────────────────────────────────────────────
// Manages friend requests, friend list, and real-time friendship updates.
// Uses the `friendships` table with statuses: pending | accepted | rejected

/**
 * Hook: useFriendships
 * Loads and subscribes to friendship data for a given user.
 */
export function useFriendships(userId) {
  const [friends, setFriends] = useState([]);       // accepted friendships with profile data
  const [incoming, setIncoming] = useState([]);      // pending requests TO this user
  const [outgoing, setOutgoing] = useState([]);      // pending requests FROM this user
  const [loading, setLoading] = useState(true);

  const fetchFriendships = useCallback(async () => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        .from("friendships")
        .select("*")
        .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`);

      if (error) { console.error("[FriendSystem] fetch error:", error); return; }

      const accepted = [];
      const inc = [];
      const out = [];

      for (const row of (data || [])) {
        if (row.status === "accepted") {
          accepted.push(row);
        } else if (row.status === "pending") {
          if (row.addressee_id === userId) inc.push(row);
          else out.push(row);
        }
      }

      // Gather all friend user IDs to fetch profiles
      const friendUserIds = accepted.map(r =>
        r.requester_id === userId ? r.addressee_id : r.requester_id
      );
      const incomingUserIds = inc.map(r => r.requester_id);
      const outgoingUserIds = out.map(r => r.addressee_id);
      const allIds = [...new Set([...friendUserIds, ...incomingUserIds, ...outgoingUserIds])];

      let profileMap = {};
      if (allIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, display_name, avatar_url, accent_color, is_online, last_seen, hide_online_status, status_message, bio")
          .in("id", allIds);
        for (const p of (profiles || [])) {
          profileMap[p.id] = p;
        }
      }

      setFriends(accepted.map(r => ({
        ...r,
        profile: profileMap[r.requester_id === userId ? r.addressee_id : r.requester_id] || null,
      })));
      setIncoming(inc.map(r => ({ ...r, profile: profileMap[r.requester_id] || null })));
      setOutgoing(out.map(r => ({ ...r, profile: profileMap[r.addressee_id] || null })));
    } catch (err) {
      console.error("[FriendSystem] fetch exception:", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchFriendships();

    // Real-time subscription — unique name per mount to avoid "callbacks after subscribe()" error
    const channelName = `friendships-${userId}-${Math.random().toString(36).slice(2, 8)}`;
    const channel = supabase
      .channel(channelName)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "friendships",
      }, () => {
        fetchFriendships();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [userId, fetchFriendships]);

  return { friends, incoming, outgoing, loading, refresh: fetchFriendships };
}

/**
 * Send a friend request
 */
export async function sendFriendRequest(fromUserId, toUserId) {
  // Check if friendship already exists
  const { data: existing } = await supabase
    .from("friendships")
    .select("id, status")
    .or(`and(requester_id.eq.${fromUserId},addressee_id.eq.${toUserId}),and(requester_id.eq.${toUserId},addressee_id.eq.${fromUserId})`);

  if (existing && existing.length > 0) {
    const row = existing[0];
    if (row.status === "accepted") return { error: "Already friends" };
    if (row.status === "pending") return { error: "Request already pending" };
    // Note: declining a request DELETES the row (see rejectFriendRequest), so a
    // 'rejected' status never persists — a re-request just falls through to the
    // INSERT below. (No dead 'rejected' branch here.)
  }

  const { error } = await supabase
    .from("friendships")
    .insert({ requester_id: fromUserId, addressee_id: toUserId, status: "pending" });
  return { error: error?.message || null };
}

/**
 * Accept a friend request
 */
export async function acceptFriendRequest(friendshipId) {
  const { error } = await supabase
    .from("friendships")
    .update({ status: "accepted" })
    .eq("id", friendshipId);
  return { error: error?.message || null };
}

/**
 * Reject a friend request
 */
export async function rejectFriendRequest(friendshipId) {
  const { error } = await supabase
    .from("friendships")
    .delete()
    .eq("id", friendshipId);
  return { error: error?.message || null };
}

/**
 * Cancel an outgoing friend request
 */
export async function cancelFriendRequest(friendshipId) {
  const { error } = await supabase
    .from("friendships")
    .delete()
    .eq("id", friendshipId);
  return { error: error?.message || null };
}

/**
 * Remove a friend (unfriend)
 */
export async function removeFriend(friendshipId) {
  const { error } = await supabase
    .from("friendships")
    .delete()
    .eq("id", friendshipId);
  return { error: error?.message || null };
}

// ─── Helper: get friendship status between two users ─────────────────────────
export function getFriendshipStatus(userId, targetId, friends, incoming, outgoing) {
  if (friends.some(f => f.profile?.id === targetId)) return "friends";
  if (incoming.some(f => f.profile?.id === targetId)) return "incoming";
  if (outgoing.some(f => f.profile?.id === targetId)) return "outgoing";
  return "none";
}

// ─── Friend Button (inline, for user lists) ──────────────────────────────────
export function FriendButton({ userId, targetId, friends, incoming, outgoing, onAction, compact = false }) {
  const { theme: t } = useContext(ThemeContext);
  const [busy, setBusy] = useState(false);

  const status = getFriendshipStatus(userId, targetId, friends, incoming, outgoing);

  const handleClick = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      // These helpers return { error } rather than throwing — the old code
      // discarded it, so a failed action was a silent no-op that still
      // refreshed the UI as if it worked.
      let actionError = null;
      if (status === "none") {
        ({ error: actionError } = await sendFriendRequest(userId, targetId) || {});
      } else if (status === "incoming") {
        const req = incoming.find(f => f.profile?.id === targetId);
        if (req) ({ error: actionError } = await acceptFriendRequest(req.id) || {});
      } else if (status === "outgoing") {
        const req = outgoing.find(f => f.profile?.id === targetId);
        if (req) ({ error: actionError } = await cancelFriendRequest(req.id) || {});
      }
      if (actionError) {
        console.error("[FriendButton] action failed:", actionError);
        alert(`Friend action failed: ${actionError}`);
        return;
      }
      if (onAction) onAction();
    } catch (err) {
      console.error("[FriendButton] error:", err);
    } finally {
      setBusy(false);
    }
  };

  if (status === "friends") {
    return compact ? (
      <span title="Friends" style={{ fontSize: "12px", opacity: 0.7 }}>💛</span>
    ) : (
      <span style={{ fontSize: "11px", color: t.accentSuccess, display: "flex", alignItems: "center", gap: "3px" }}>
        💛 Friends
      </span>
    );
  }

  const btnStyle = {
    background: "none",
    border: "none",
    cursor: busy ? "wait" : "pointer",
    fontSize: compact ? "12px" : "11px",
    padding: compact ? "2px 6px" : "4px 10px",
    borderRadius: "6px",
    transition: "all 0.15s",
    opacity: busy ? 0.5 : 1,
    flexShrink: 0,
    fontWeight: 600,
  };

  if (status === "incoming") {
    return compact ? (
      <button onClick={handleClick} disabled={busy}
        style={{ ...btnStyle, color: t.accentSuccess, border: `1px solid ${t.accentSuccess}55`, background: `${t.accentSuccess}18` }}
        title="They sent you a friend request — click to accept">
        ✓ Accept
      </button>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "2px" }}>
        <div style={{ fontSize: "10px", color: t.accentSuccess, fontStyle: "italic" }}>sent you a request</div>
        <button onClick={handleClick} disabled={busy}
          style={{ ...btnStyle, color: t.accentSuccess, border: `1px solid ${t.accentSuccess}55`, background: `${t.accentSuccess}18` }}>
          ✓ Accept
        </button>
      </div>
    );
  }

  if (status === "outgoing") {
    return compact ? (
      <button onClick={handleClick} disabled={busy}
        style={{ ...btnStyle, color: t.textMuted, border: `1px solid ${t.borderMedium}` }}
        title="Friend request sent — click to cancel">
        ⏳ Sent
      </button>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "2px" }}>
        <div style={{ fontSize: "10px", color: t.textMuted, fontStyle: "italic" }}>request sent</div>
        <button onClick={handleClick} disabled={busy}
          style={{ ...btnStyle, color: t.textMuted, border: `1px solid ${t.borderMedium}` }}>
          Cancel
        </button>
      </div>
    );
  }

  // status === "none"
  return (
    <button onClick={handleClick} disabled={busy}
      style={{ ...btnStyle, color: t.accentGold, border: `1px solid ${t.accentGold}44` }}
      title="Send friend request">
      {compact ? "+ Add" : "Add Friend"}
    </button>
  );
}

// ─── Friends Tab Panel (for ProfileSettings) ─────────────────────────────────
export function FriendsPanel({ userId }) {
  const { theme: t } = useContext(ThemeContext);
  const { friends, incoming, outgoing, loading, refresh } = useFriendships(userId);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  const inputStyle = {
    background: "var(--ds-input, #0d0708)", color: "var(--ds-ink-hi, #f1e7e3)",
    border: "1px solid var(--ds-line, #3a2128)", borderRadius: "var(--ds-r-md, 14px)",
    padding: "10px 12px", fontSize: "14px", outline: "none", width: "100%", boxSizing: "border-box",
  };

  const sectionLabel = {
    fontSize: "11px", color: t.textMuted, textTransform: "uppercase",
    letterSpacing: "1px", marginBottom: "8px", fontWeight: 700,
  };

  const cardStyle = {
    display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px",
    background: "var(--ds-surface-2, #1d1013)", borderRadius: "var(--ds-r-md, 14px)", marginBottom: "6px", border: "1px solid var(--ds-line-soft, #2a161b)",
  };

  const showMsg = (msg) => {
    setActionMsg(msg);
    setTimeout(() => setActionMsg(null), 2500);
  };

  // Search users
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchResults([]);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, display_name, avatar_url, accent_color, is_online, last_seen, bio")
        .ilike("display_name", `%${searchQuery.trim()}%`)
        .neq("id", userId)
        .limit(10);
      if (error) throw error;
      setSearchResults(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[FriendsPanel] search error:", err);
      setSearchResults([]);
      showMsg("Search failed — please try again");
    } finally {
      setSearching(false);
    }
  };

  const handleAccept = async (id) => {
    const { error } = await acceptFriendRequest(id);
    if (error) showMsg(error);
    else { showMsg("Friend request accepted!"); refresh(); }
  };

  const handleReject = async (id) => {
    const { error } = await rejectFriendRequest(id);
    if (error) showMsg(error);
    else { showMsg("Request declined"); refresh(); }
  };

  const handleCancel = async (id) => {
    const { error } = await cancelFriendRequest(id);
    if (error) showMsg(error);
    else { showMsg("Request cancelled"); refresh(); }
  };

  const handleRemove = async (id) => {
    const { error } = await removeFriend(id);
    if (error) showMsg(error);
    else { showMsg("Friend removed"); refresh(); }
  };

  const handleSendRequest = async (targetId) => {
    const { error } = await sendFriendRequest(userId, targetId);
    if (error) showMsg(error);
    else { showMsg("Friend request sent!"); refresh(); }
  };

  const renderAvatar = (profile, size = 36) => (
    <div style={{
      width: `${size}px`, height: `${size}px`, borderRadius: "50%", flexShrink: 0,
      background: profile?.avatar_url
        ? `url(${profile.avatar_url}) center/cover`
        : `${profile?.accent_color || t.accentGold}33`,
      display: "flex", alignItems: "center", justifyContent: "center", fontSize: `${size * 0.4}px`,
    }}>
      {!profile?.avatar_url && "👤"}
    </div>
  );

  const renderProfile = (profile) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        fontWeight: 600, color: profile?.accent_color || t.textPrimary, fontSize: "13px",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {profile?.display_name || "Unknown"}
      </div>
      {profile?.bio && (
        <div style={{ fontSize: "11px", color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {profile.bio}
        </div>
      )}
    </div>
  );

  const actionBtn = (label, color, onClick) => {
    // color is a theme token (t.accentSuccess / accentGold / accentDanger), so
    // alpha it directly — there are no --ds-success/-danger-aNN tokens, and the
    // old var() forms fell through to a hardcoded black wash on hover.
    return (
      <button onClick={onClick} style={{
        background: `${color}22`, border: `1px solid ${color}44`, color,
        borderRadius: "var(--ds-r-md, 14px)", padding: "5px 12px", fontSize: "11px", fontWeight: 600,
        cursor: "pointer", transition: "all 0.15s", flexShrink: 0,
      }}
        onMouseEnter={e => { e.currentTarget.style.background = `${color}44`; }}
        onMouseLeave={e => { e.currentTarget.style.background = `${color}22`; }}
      >
        {label}
      </button>
    );
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: "30px", color: t.textMuted }}>Loading friends...</div>;
  }

  return (
    <div>
      {/* Action feedback */}
      {actionMsg && (
        <div style={{
          padding: "8px 14px", borderRadius: "8px", background: `${t.accentSuccess}22`,
          color: t.accentSuccess, fontSize: "12px", marginBottom: "12px", textAlign: "center",
          animation: "fadeSlideIn 0.2s ease-out",
        }}>
          {actionMsg}
        </div>
      )}

      {/* Search for users */}
      <div style={{ marginBottom: "20px" }}>
        <div className="bq-title" style={sectionLabel}>Find Players</div>
        <div style={{ display: "flex", gap: "8px" }}>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Search by display name..."
            style={{ ...inputStyle, flex: 1 }}
          />
          <button onClick={handleSearch} disabled={searching} style={{
            background: "var(--ds-gold, #c4a882)", color: "#1a0e10", border: "none", borderRadius: "var(--ds-r-pill, 999px)",
            padding: "8px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer",
            opacity: searching ? 0.5 : 1, transition: "all 0.15s ease",
          }}>
            {searching ? "..." : "Search"}
          </button>
        </div>
        {searchResults.length > 0 && (
          <div style={{ marginTop: "10px" }}>
            {searchResults.map(p => {
              const status = getFriendshipStatus(userId, p.id, friends, incoming, outgoing);
              return (
                <div key={p.id} style={cardStyle}>
                  {renderAvatar(p)}
                  {renderProfile(p)}
                  {status === "friends" && <span style={{ fontSize: "11px", color: t.accentSuccess }}>Friends</span>}
                  {status === "incoming" && actionBtn("Accept", t.accentSuccess, () => {
                    const req = incoming.find(f => f.profile?.id === p.id);
                    if (req) handleAccept(req.id);
                  })}
                  {status === "outgoing" && <span style={{ fontSize: "11px", color: t.textMuted }}>Pending</span>}
                  {status === "none" && actionBtn("Add Friend", t.accentGold, () => handleSendRequest(p.id))}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Incoming Requests — prominent banner */}
      {incoming.length > 0 && (
        <div className="bq-leaf" style={{
          marginBottom: "20px",
          background: "var(--ds-success-a12, rgba(92,186,125,0.07))",
          border: "1px solid var(--ds-success-a40, rgba(92,186,125,0.25))",
          borderRadius: "var(--ds-r-lg, 20px)",
          padding: "14px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <div style={{
              background: t.accentSuccess, color: "#fff", borderRadius: "50%",
              width: "20px", height: "20px", display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: "12px", fontWeight: 700, flexShrink: 0,
            }}>
              {incoming.length}
            </div>
            <div className="bq-title" style={{ ...sectionLabel, margin: 0, color: t.accentSuccess }}>
              Friend {incoming.length === 1 ? "Request" : "Requests"}
            </div>
          </div>
          {incoming.map(req => (
            <div key={req.id} style={{ ...cardStyle, background: "var(--ds-success-a0a, rgba(92,186,125,0.04))", border: "1px solid var(--ds-success-a20, rgba(92,186,125,0.125))", marginBottom: "8px" }}>
              {renderAvatar(req.profile)}
              <div style={{ flex: 1, minWidth: 0 }}>
                {renderProfile(req.profile)}
                <div style={{ fontSize: "11px", color: t.accentSuccess, marginTop: "2px", fontStyle: "italic" }}>
                  wants to be your friend
                </div>
              </div>
              <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                {actionBtn("✓ Accept", t.accentSuccess, () => handleAccept(req.id))}
                {actionBtn("Decline", t.accentDanger || "#ed4245", () => handleReject(req.id))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Outgoing Requests */}
      {outgoing.length > 0 && (
        <div style={{ marginBottom: "20px" }}>
          <div className="bq-title" style={sectionLabel}>
            Sent Requests ({outgoing.length})
          </div>
          {outgoing.map(req => (
            <div key={req.id} style={cardStyle}>
              {renderAvatar(req.profile)}
              <div style={{ flex: 1, minWidth: 0 }}>
                {renderProfile(req.profile)}
                <div style={{ fontSize: "11px", color: t.textMuted, marginTop: "2px", fontStyle: "italic" }}>
                  waiting for their response
                </div>
              </div>
              {actionBtn("Cancel", t.textMuted, () => handleCancel(req.id))}
            </div>
          ))}
        </div>
      )}

      {/* Friends List */}
      <div>
        <div className="bq-title" style={sectionLabel}>
          Friends ({friends.length})
        </div>
        {friends.length === 0 ? (
          <div style={{
            textAlign: "center", padding: "24px", color: t.textMuted, fontSize: "13px",
            background: t.bgHover, borderRadius: "10px",
          }}>
            No friends yet. Search for players above to send a request!
          </div>
        ) : (
          friends.map(f => (
            <div key={f.id} style={cardStyle}>
              <div style={{ position: "relative" }}>
                {renderAvatar(f.profile)}
                {isUserOnline(f.profile) && (
                  <div style={{
                    position: "absolute", bottom: "0", right: "0", width: "10px", height: "10px",
                    borderRadius: "50%", background: t.accentSuccess, border: `2px solid ${t.bgHover}`,
                  }} />
                )}
              </div>
              {renderProfile(f.profile)}
              <button
                onClick={() => handleRemove(f.id)}
                style={{
                  background: "none", border: "none", color: t.textMuted,
                  cursor: "pointer", fontSize: "14px", padding: "4px", flexShrink: 0,
                  opacity: 0.5, transition: "opacity 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.color = t.accentDanger || "#ed4245"; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = "0.5"; e.currentTarget.style.color = t.textMuted; }}
                title="Remove friend"
              >
                ✕
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── User Profile Modal ───────────────────────────────────────────────────────
// Full profile popup that appears when you click on any user.
// Shows avatar, bio, online status, friend actions, DM button.
export function UserProfileModal({
  profile,           // { id, display_name, avatar_url, accent_color, bio, status_message, is_online, last_seen }
  currentUserId,
  friends,
  incoming,
  outgoing,
  onAction,          // called after friend action
  onClose,
  onDM,             // (userId) => void
  formatLastActive,  // optional time formatter
}) {
  const { theme: t } = useContext(ThemeContext);
  if (!profile) return null;

  const isMe = profile.id === currentUserId;
  const accent = profile.accent_color || t.accentGold || "var(--ds-gold, #c4a882)";
  const isOnline = isUserOnline(profile); // stale heartbeats are NOT online

  const friend = friends?.find(f => f.profile?.id === profile.id);
  const friendshipId = friend?.id;

  const formatTime = formatLastActive || ((ts) => {
    if (!ts) return "a while ago";
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  });

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9500,
        background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px) saturate(1.15)", WebkitBackdropFilter: "blur(8px) saturate(1.15)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "20px",
      }}
      onClick={onClose}
    >
      <div
        className="bq-leaf"
        style={{
          background: `linear-gradient(160deg, var(--ds-surface-1, #150b0d) 0%, var(--ds-surface-0, #0a0506) 100%)`,
          border: `1px solid ${accent}33`,
          borderRadius: "var(--ds-r-xl, 28px)",
          width: "100%", maxWidth: "340px",
          boxShadow: `var(--ds-shadow-3), inset 0 1px 0 var(--ds-glass-light, rgba(255,255,255,0.055))`,
          overflow: "hidden",
          animation: "modalFadeIn 0.2s ease-out",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Banner + avatar */}
        <div className="bq-bar" style={{
          height: "80px",
          background: `linear-gradient(135deg, var(--ds-surface-2, #1d1013) 0%, var(--ds-surface-1, #150b0d) 100%)`,
          borderBottom: `1px solid var(--ds-line-soft, #2a161b)`,
          position: "relative",
        }}>
          {/* Close */}
          <button
            onClick={onClose}
            style={{
              position: "absolute", top: "10px", right: "10px",
              background: "rgba(0,0,0,0.4)", border: "none", borderRadius: "50%",
              width: "28px", height: "28px", color: "#aaa", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px",
            }}
          >✕</button>

          {/* Avatar */}
          <div style={{
            position: "absolute", bottom: "-28px", left: "20px",
            width: "60px", height: "60px", borderRadius: "50%",
            border: `3px solid var(--ds-surface-0, #0a0506)`,
            background: profile.avatar_url ? `url(${profile.avatar_url}) center/cover` : `${accent}22`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "22px", flexShrink: 0,
          }}>
            {!profile.avatar_url && "👤"}
            {/* Online indicator */}
            <div style={{
              position: "absolute", bottom: "2px", right: "2px",
              width: "12px", height: "12px", borderRadius: "50%",
              background: isOnline ? "#43b581" : "#747f8d",
              border: "2px solid #1a1212",
            }} />
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "36px 20px 20px" }}>
          {/* Name + status */}
          <div style={{ marginBottom: "4px" }}>
            <div style={{ fontSize: "18px", fontWeight: 700, color: accent, fontFamily: "'Cinzel', serif", letterSpacing: "0.5px", textTransform: "uppercase" }}>
              {profile.display_name}
              {isMe && <span style={{ fontSize: "12px", color: t.textMuted, fontWeight: 400, marginLeft: "8px", fontFamily: "sans-serif" }}>(You)</span>}
            </div>
            <div style={{ fontSize: "11px", color: isOnline ? "var(--ds-success, #5cba7d)" : "var(--ds-ink-faint, #6e5a54)", marginTop: "2px", display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: isOnline ? "var(--ds-success, #5cba7d)" : "var(--ds-ink-low, #9c8983)", display: "inline-block", flexShrink: 0 }} />
              {isOnline ? "Online now" : profile.hide_online_status ? "Offline" : `Last seen ${formatTime(profile.last_seen)}`}
            </div>
          </div>

          {/* Status message */}
          {profile.status_message && (
            <div style={{
              fontSize: "13px", color: "var(--ds-ink, #cdbbb4)", fontStyle: "italic",
              padding: "8px 10px", background: `var(--ds-surface-2, #1d1013)`, borderRadius: "var(--ds-r-md, 14px)",
              border: `1px solid var(--ds-line-soft, #2a161b)`, marginTop: "10px",
            }}>
              "{profile.status_message}"
            </div>
          )}

          {/* Bio */}
          {profile.bio && (
            <div style={{ fontSize: "12px", color: "var(--ds-ink-low, #9c8983)", marginTop: "10px", lineHeight: 1.5 }}>
              {profile.bio}
            </div>
          )}

          {/* Divider */}
          <div style={{ height: "1px", background: "var(--ds-line-soft, #2a161b)", margin: "14px 0" }} />

          {/* Action buttons */}
          {!isMe && (
            <div style={{ display: "flex", gap: "8px" }}>
              {/* Friend button — full-size */}
              <FriendButton
                userId={currentUserId}
                targetId={profile.id}
                friends={friends}
                incoming={incoming}
                outgoing={outgoing}
                onAction={onAction}
                compact={false}
              />

              {/* Unfriend if friends */}
              {friendshipId && (
                <button
                  onClick={async () => { await removeFriend(friendshipId); onAction?.(); }}
                  style={{
                    background: "none", border: `1px solid ${t.borderMedium}33`,
                    borderRadius: "6px", padding: "5px 10px",
                    color: t.textMuted, cursor: "pointer", fontSize: "11px",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = "#ed4245"; e.currentTarget.style.borderColor = "#ed424544"; }}
                  onMouseLeave={e => { e.currentTarget.style.color = t.textMuted; e.currentTarget.style.borderColor = `${t.borderMedium}33`; }}
                  title="Remove friend"
                >
                  Unfriend
                </button>
              )}

              {/* DM button */}
              <button
                onClick={() => { onDM?.(profile.id); onClose?.(); }}
                style={{
                  background: `var(--ds-gold-a18, rgba(196,168,130,0.09))`, border: "1px solid var(--ds-gold-a33, rgba(196,168,130,0.2))",
                  borderRadius: "var(--ds-r-md, 14px)", padding: "6px 14px",
                  color: "var(--ds-gold, #c4a882)", cursor: "pointer", fontSize: "12px", fontWeight: 600,
                  transition: "all 0.15s", marginLeft: "auto",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--ds-gold-a30, rgba(196,168,130,0.19))"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--ds-gold-a18, rgba(196,168,130,0.09))"; }}
              >
                💌 Message
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
